import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Loading from 'components/shared-components/Loading';
import ParametersProvider from 'context/ParametersProvider/ParametersProvider';
import { APP_PREFIX_PATH } from 'configs/AppConfig';
import { AUTH_TOKEN } from 'constants/AppConstant';
import cookies from 'utils/cookies';

const ReclamationsList = lazy(() => import('./reclamations/list'));
const ReclamationsEdit = lazy(() => import('./reclamations/edit'));
const ReclamationsCopy = lazy(() => import('./orders/copy_reclamation'));
const ReclamationsAdd = lazy(() => import('./orders/add_reclamations'));
const ClientsList = lazy(() => import('./clients/list'));
const ClientsAdd = lazy(() => import('./clients/add'));
const ClientsEdit = lazy(() => import('./clients/edit'));
const ProductsList = lazy(() => import('./products/list'));
const ProductsAdd = lazy(() => import('./products/add'));
const ProductsEdit = lazy(() => import('./products/edit'));
const ProductsCopy = lazy(() => import('./products/copy'));
const ProductPreview = lazy(() => import('./products/preview'));
const CarBrandsList = lazy(() => import('./car-brands/list'));
const CarBrandsAdd = lazy(() => import('./car-brands/add'));
const CarBrandsEdit = lazy(() => import('./car-brands/edit'));
const CarModelsList = lazy(() => import('./car-models/list'));
const CarModelsAdd = lazy(() => import('./car-models/add'));
const CarModelsEdit = lazy(() => import('./car-models/edit'));
const TurbinesList = lazy(() => import('./turbines/list'));
const TurbinesAdd = lazy(() => import('./turbines/add'));
const TurbinesEdit = lazy(() => import('./turbines/edit'));
const TurbinesAlias = lazy(() => import('./turbines/alias'));
const CarsList = lazy(() => import('./cars/list'));
const CarsAdd = lazy(() => import('./cars/add'));
const CarsEdit = lazy(() => import('./cars/edit'));
const UsersList = lazy(() => import('./users/list'));
const UsersAdd = lazy(() => import('./users/add'));
const UsersEdit = lazy(() => import('./users/edit'));
const ShippingAddressesList = lazy(() => import('./shipping-addresses/list'));
const ShippingAddressesAdd = lazy(() => import('./shipping-addresses/add'));
const ShippingAddressesEdit = lazy(() => import('./shipping-addresses/edit'));
const SettingsList = lazy(() => import('./settings/list'));
const Statistics = lazy(() => import('./statistics'));
const StatusStatistics = lazy(() => import('./status-statistics'));
const OrderStatistics = lazy(() => import('./orderStatistics'));
const MarginsList = lazy(() => import('./margins/list'));
const MarginsAdd = lazy(() => import('./margins/add'));
const MarginsEdit = lazy(() => import('./margins/edit'));
const DiscountCodesList = lazy(() => import('./discount-codes/list'));
const DiscountCodesAdd = lazy(() => import('./discount-codes/add'));
const DiscountCodesEdit = lazy(() => import('./discount-codes/edit'));
const DiscountCodesVerify = lazy(() => import('./discount-codes/verify'));
const AllegroSettingsList = lazy(() => import('./allegro-settings/list'));
const AllegroSettingsSingle = lazy(() => import('./allegro-settings/single'));
const AllegroSettingsAddConditions = lazy(() => import('./allegro-settings/addConditions'));
const AllegroSettingsAddProducers = lazy(() => import('./allegro-settings/addProducers'));
const ImportedProductsImagesList = lazy(() => import('./imported-products-images/list'));
const ImportSyncLogsList = lazy(() => import('./import-sync-logs/list'));
const MobileApps = lazy(() => import('./mobile-apps/list'));
const WarehousesNetPriceSum = lazy(() => import('./warehouses/net-price-sum'));
const WarehouseInventoriesList = lazy(() => import('./warehouse-inventories/list'));
const WarehouseInventoriesAdd = lazy(() => import('./warehouse-inventories/add'));
const WarehouseInventoriesEdit = lazy(() => import('./warehouse-inventories/edit'));
const WarehouseInventoriesPreview = lazy(() => import('./warehouse-inventories/preview'));

const OrdersList = lazy(() => import('./orders/list'));
const OrdersAdd = lazy(() => import('./orders/add'));
const OrderCopy = lazy(() => import('./orders/copy'));
const OrdersPreview = lazy(() => import('./orders/preview'));
const OrdersEdit = lazy(() => import('./orders/edit'));
const PackageToClient = lazy(() => import('./orders/package_to_client'));
const PackageToMagazine = lazy(() => import('./orders/package_to_magazine'));
const TakenFromWarehouseList = lazy(() => import('./taken-from-warehouse/list'));

export const AppViews = () => {
  const accessToken = cookies.get(AUTH_TOKEN);
  const navigate = useNavigate();
  if (!accessToken) {
    navigate('/auth/login');
    return null;
  }
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/orders" element={<ParametersProvider key="orders" />}>
          <Route index element={<OrdersList />} />
          <Route path="add" element={<OrdersAdd />} />
          <Route path="preview/:id" element={<OrdersPreview />} />
          <Route path="edit/:id" element={<OrdersEdit />} />
          <Route path="copy/:id" element={<OrderCopy />} />
          <Route path=":id/reclamations/add" element={<ReclamationsAdd />} />
          <Route path=":id/package-to-client" element={<PackageToClient />} />
          <Route path=":id/package-to-magazine" element={<PackageToMagazine />} />
        </Route>
        <Route path="/taken-from-warehouse" element={<ParametersProvider key="taken-from-warehouse" />}>
          <Route index element={<TakenFromWarehouseList />} />
        </Route>
        <Route path="/reclamations" element={<ParametersProvider key="reclamations" />}>
          <Route index element={<ReclamationsList />} />
          <Route path="edit/:id" element={<ReclamationsEdit />} />
          <Route path=":reclamation/add/:id" element={<ReclamationsCopy />} />
        </Route>
        <Route path="/clients" element={<ParametersProvider key="clients" />}>
          <Route index element={<ClientsList />} />
          <Route path="add" element={<ClientsAdd />} />
          <Route path="edit/:id" element={<ClientsEdit />} />
        </Route>
        <Route path="/products" element={<ParametersProvider key="products" />}>
          <Route index element={<ProductsList />} />
          <Route path="add" element={<ProductsAdd />} />
          <Route path="edit/:id" element={<ProductsEdit />} />
          <Route path="copy/:id" element={<ProductsCopy />} />
          <Route path="preview/:id" element={<ProductPreview />} />
        </Route>
        <Route path="/car-brands" element={<ParametersProvider key="car-brands" />}>
          <Route index element={<CarBrandsList />} />
          <Route path="add" element={<CarBrandsAdd />} />
          <Route path="edit/:id" element={<CarBrandsEdit />} />
        </Route>
        <Route path="/car-models" element={<ParametersProvider key="car-models" />}>
          <Route index element={<CarModelsList />} />
          <Route path="add" element={<CarModelsAdd />} />
          <Route path="edit/:id" element={<CarModelsEdit />} />
        </Route>
        <Route path="/turbines" element={<ParametersProvider key="turbines" />}>
          <Route index element={<TurbinesList />} />
          <Route path="add" element={<TurbinesAdd />} />
          <Route path="edit/:id" element={<TurbinesEdit />} />
          <Route path="alias/:id" element={<TurbinesAlias />} />
        </Route>
        <Route path="/cars" element={<ParametersProvider key="cars" />}>
          <Route index element={<CarsList />} />
          <Route path="add" element={<CarsAdd />} />
          <Route path="edit/:id" element={<CarsEdit />} />
        </Route>
        <Route path="/users" element={<ParametersProvider key="users" />}>
          <Route index element={<UsersList />} />
          <Route path="add" element={<UsersAdd />} />
          <Route path="edit/:id" element={<UsersEdit />} />
        </Route>
        <Route path="/shipping-addresses" element={<ParametersProvider key="shipping-addresses" />}>
          <Route index element={<ShippingAddressesList />} />
          <Route path="add" element={<ShippingAddressesAdd />} />
          <Route path="edit/:id" element={<ShippingAddressesEdit />} />
        </Route>
        <Route path="/settings" element={<SettingsList />} />
        <Route path="/statistics" element={<ParametersProvider key="statistics" />}>
          <Route index element={<Statistics />} />
        </Route>
        <Route path="/status-statistics" element={<ParametersProvider key="status-statistics" />}>
          <Route index element={<StatusStatistics />} />
        </Route>
        <Route path="/order-statistics" element={<OrderStatistics />} />
        <Route path="/margins" element={<ParametersProvider key="margins" />}>
          <Route index element={<MarginsList />} />
          <Route path="add" element={<MarginsAdd />} />
          <Route path="edit/:id" element={<MarginsEdit />} />
        </Route>
        <Route path="/discount-codes" element={<ParametersProvider key="discount-codes" />}>
          <Route index element={<DiscountCodesList />} />
          <Route path="add" element={<DiscountCodesAdd />} />
          <Route path="edit/:id" element={<DiscountCodesEdit />} />
        </Route>
        <Route path="/discount-codes/verify" element={<DiscountCodesVerify />} />
        <Route path="/allegro-settings" element={<ParametersProvider key="allegro-settings" />}>
          <Route index element={<AllegroSettingsList />} />
          <Route path=":user" element={<AllegroSettingsSingle />} />
          <Route path=":user/conditions" element={<AllegroSettingsAddConditions />} />
          <Route path=":user/producers" element={<AllegroSettingsAddProducers />} />
        </Route>
        <Route
          path="/imported-products-images"
          element={<ParametersProvider key="imported-products-images" />}
        >
          <Route index element={<ImportedProductsImagesList />} />
        </Route>
        <Route path="/import-sync-logs" element={<ParametersProvider key="import-sync-logs" />}>
          <Route index element={<ImportSyncLogsList />} />
        </Route>
        <Route path="/mobile-apps" element={<MobileApps />} />
        <Route path="/warehouses/net-price-sum" element={<ParametersProvider key="warehouses-net-price-sum" />}>
          <Route index element={<WarehousesNetPriceSum />} />
        </Route>
        <Route path="/warehouse-inventories" element={<ParametersProvider key="warehouse-inventories" />}>
          <Route index element={<WarehouseInventoriesList />} />
          <Route path="add" element={<WarehouseInventoriesAdd />} />
          <Route path="edit/:id" element={<WarehouseInventoriesEdit />} />
          <Route path="preview/:id" element={<WarehouseInventoriesPreview />} />
        </Route>
        <Route path="*" element={<Navigate to={`${APP_PREFIX_PATH}/orders`} replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppViews;
